import React, { useState, useEffect } from "react";
import { Row, Col, Form, Select, Button, Icon, Dropdown, Menu } from "antd";
import ReusableInput from "./ReusableInput";
import Select_v2 from "./Select_v2";
import InfoTooltip from "./InfoTooltip";
import { RefNumTooltipIcon } from "../../common/CustomIcon";
import _ from "lodash";
import I18n from "../../common/I18n";
import { SideBySideFormItem } from "../../common/UIComponents";
import useItNumberSync from "../../hooks/useItNumberSync";

function ReferenceNumberSection(props) {
    const {
        referencesData = [],
        getDataRef,
        updatedAccountId,
        setFieldsValue,
        details = {},
        actionType = "create",
        handleRefNumberChange,
        availableSpace,
        orgRules = {},
        currentOrgCode = "",
    } = props;
    const [accountId, setAccountId] = useState(updatedAccountId);
    const [sharedItNumber, setSharedItNumber] = useItNumberSync(details?.customer_order_id);

    const requiredFields = (orgRules?.org_billing_rules?.require_fields_as_mandatory || "")
        .split(",")
        .map(f => f.trim().toLowerCase())
        .filter(f => f.length > 0);

    const isFieldMandatory = (field) => requiredFields.includes(field.toLowerCase());

    const [formValues, setFormValues] = useState(() => {
        const defaultRefType = referencesData.length > 0
            ? referencesData[0].order_reference_type
            : "Ref 1 Type";
        return {
            hawb: "",
            mawb: "",
            it_number: "",
            reference_1: "",
            reference_1_type: details.reference_1_type || defaultRefType,
            reference_2: "",
            reference_2_type: "Ref 2 Type",
            references: [],
            ...(_.pick(details, ["hawb","mawb", "it_number", "reference_1", "reference_2", "reference_2_type","references"])),
        };
    });

    const [showReferences, setShowReferences] = useState(false);
    const [hoveredField, setHoveredField] = useState(null);

    useEffect(() => {
        setShowReferences(availableSpace);
      }, [availableSpace]);

    useEffect(() => {
        if (["view", "update"].includes(actionType)) {
            setFieldsValue({
                account_id: details.account_id,
                warehouse_id: details.warehouse_id,
            });
        }
    }, []);

    useEffect(() => {
        if (["view", "update"].includes(actionType)) {
            setFormValues(prev => ({
                ...prev,
                reference_1_type: details.reference_1_type || (referencesData.length > 0 ? referencesData[0].order_reference_type : "Ref 1 Type"),
                ...(_.pick(details, ["hawb","mawb", "reference_1", "reference_2", "reference_2_type","references"])),
                it_number: sharedItNumber ?? details.it_number ?? "",
            }));
        }
    }, [details, referencesData]);

    useEffect(() => {
        if (updatedAccountId !== accountId) {
            setAccountId(updatedAccountId)
            setFormValues({
                ...formValues,
                account_id: updatedAccountId,
                reference_1_type: referencesData.length > 0 ? referencesData[0].order_reference_type : "",
                reference_1: "",
                reference_2_type: "",
                reference_2: "",
            });
            setFieldsValue({
                account_id: updatedAccountId,
                reference_1_type: "",
                reference_1: "",
                reference_2_type: "",
                reference_2: "",
            });
        }
    }, [updatedAccountId]);

    useEffect(() => {
        handleRefNumberChange(formValues)
        if (getDataRef) {
            getDataRef.current = () => formValues;
        }
    }, [formValues, getDataRef]);

    useEffect(() => {
        if (sharedItNumber === undefined) return;

        setFormValues(prev => (
            prev.it_number === sharedItNumber ? prev : { ...prev, it_number: sharedItNumber }
        ));
    }, [sharedItNumber]);

    useEffect(() => {
        if (referencesData.length > 0) {
            setFormValues((prev) => {
                if (prev.reference_1_type === "Ref 1 Type" || !prev.reference_1_type) {
                    return { ...prev, reference_1_type: referencesData[0].order_reference_type };
                }
                return prev;
            });
        }
    }, [referencesData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevState) => ({
            ...prevState,
            [name]: value,
        }));
        if (name === "it_number") {
            setSharedItNumber(value);
        }
    };

    const handleSelectChange = (value, fieldName) => {
        setFormValues((prevState) => ({
            ...prevState,
            [fieldName]: value,
        }));
    };
    
    return (
        <div className="RefNumbersContainer" style={{ marginBottom: "16px" }} data-testid="reference-section">
            <div className="heading-3-large">Ref Numbers</div>
            <Row>
                <Col span={24}>
                    <Row style={{ marginBottom: "4px" }}>
                        <Col span={11} style={{ lineHeight: "30px",}} onMouseEnter={() => setHoveredField('hawb')}
                            onMouseLeave={() => setHoveredField(null)}>
                            <div
                                className="RefNumbersStyle"
                                style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "10px" }} >
                                    <span>
                                        HAWB {isFieldMandatory("hawb") && <span style={{ color: "red" }}>*</span> }
                                    </span>
                                {hoveredField === 'hawb' && (
                                    <InfoTooltip
                                        label="HAWB"
                                        icon={<RefNumTooltipIcon />}
                                        message={[
                                            "A HAWB, or House Air Waybill, is a document from a freight forwarder detailing a shipment's contents, shipper, and recipient for air cargo. It helps track and manage individual shipments."
                                        ]}
                                    />
                                )}
                            </div>
                        </Col>
                        <Col span={13}>
                            <ReusableInput
                                data-testid="hawb-input"
                                name="hawb"
                                value={formValues.hawb}
                                onChange={handleChange}
                                capsOn={true}
                            />
                        </Col>
                    </Row>
                    <Row style={{ marginBottom: "4px" }}>
                        <Col span={11} style={{ lineHeight: "30px" }}
                        onMouseEnter={() => setHoveredField('mawb')}
                        onMouseLeave={() => setHoveredField(null)}>
                            <div
                                className="RefNumbersStyle"
                                style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}
                                >
                                <span>
                                    MAWB {isFieldMandatory("mawb") && <span style={{ color: "red" }}>*</span>}
                                </span>
                                {hoveredField === 'mawb' && (
                                    <InfoTooltip
                                        label="MAWB"
                                        icon={<RefNumTooltipIcon />}
                                        message={[
                                            "A MAWB, or Master Air Waybill, is an airline-issued document for a consolidated shipment. Using the same MAWB number on multiple orders links them for easy tracking."
                                        ]}
                                    />
                                )}
                            </div>
                        </Col>
                        <Col span={13}>
                            <ReusableInput
                                data-testid="mawb-input"
                                name="mawb"
                                value={formValues.mawb}
                                onChange={handleChange}
                                capsOn={true}
                            />
                        </Col>
                    </Row>
                    {currentOrgCode === "MWE" && (
                    <Row style={{ marginBottom: "4px" }}>
                        <Col span={11} style={{ lineHeight: "30px" }}
                        onMouseEnter={() => setHoveredField('it_number')}
                        onMouseLeave={() => setHoveredField(null)}>
                            <div
                                className="RefNumbersStyle"
                                style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}
                                >
                                <span>
                                    IT Number {isFieldMandatory("it_number") && <span style={{ color: "red" }}>*</span>}
                                </span>
                                {hoveredField === 'it_number' && (
                                    <InfoTooltip
                                        label="IT Number"
                                        icon={<RefNumTooltipIcon />}
                                        message={[
                                            "An IT (Immediate Transportation) Number is used to track shipments moving in-bond under customs supervision."
                                        ]}
                                    />
                                )}
                            </div>
                        </Col>
                        <Col span={13}>
                            <ReusableInput
                                data-testid="it-number-input"
                                name="it_number"
                                value={formValues.it_number}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                    )}

                            <Row style={{ marginBottom: "4px" }}>
                                <Col span={11} style={{ lineHeight: "30px" }} onMouseEnter={() => setHoveredField('reference1')}
                                    onMouseLeave={() => setHoveredField(null)}>
                                    <div className="RefNumbersStyle">
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
                                            <Dropdown
                                                overlay={
                                                    <Menu>
                                                        <Menu.Item key="default">
                                                            <div
                                                                onClick={() => handleSelectChange("Ref 1 Type", "reference_1_type")}
                                                                onMouseEnter={() => setHoveredField('reference1')}
                                                                onMouseLeave={() => setHoveredField(null)}
                                                            >
                                                                Ref 1 Type
                                                            </div>
                                                        </Menu.Item>
                                                        {referencesData
                                                            ?.filter(
                                                                (rec) => rec.order_reference_type !== formValues.reference_2_type
                                                            )
                                                            .map((rec) => (
                                                                <Menu.Item key={rec.order_reference_type}>
                                                                    <div
                                                                        onClick={() =>
                                                                            handleSelectChange(rec.order_reference_type, "reference_1_type")
                                                                        }
                                                                        style={{ cursor: "pointer" }}
                                                                    >
                                                                        {rec.order_reference_type}
                                                                    </div>
                                                                </Menu.Item>
                                                            ))}
                                                    </Menu>
                                                }
                                                trigger={["click"]}
                                            >
                                                <div style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#576ec1" }}>
                                                    <span>{formValues.reference_1_type || "Ref Type"}</span>
                                                    <Icon style={{ marginLeft: "4px", color: "#576ec1" }} type="caret-down" />
                                                </div>
                                            </Dropdown>
                                            <span>
                                                {isFieldMandatory("reference_1") && <span style={{ color: "red" }}>*</span>}
                                            </span>
                                        </div>
                                        {hoveredField === 'reference1' && <InfoTooltip label="Reference Types"
                                            icon={<RefNumTooltipIcon />}
                                            message={[
                                                "Reference Types are customizable labels set in Org. Settings > Order Settings, ideal for operations differing from industry standards."
                                            ]} />}
                                    </div>
                                </Col>
                                <Col span={13}>
                                    <ReusableInput
                                        data-testid="reference-1-input"
                                        name="reference_1"
                                        value={formValues.reference_1}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Row>
                    {showReferences && (
                        <>
                            <Row style={{ marginBottom: "4px" }}>
                                <Col span={11}
                                    onMouseEnter={() => setHoveredField('reference2')}
                                    onMouseLeave={() => setHoveredField(null)}>
                                    <div className="RefNumbersStyle">
                                         <div style={{ display: "inline-flex", alignItems: "center", gap: "2px"}}>
                                            <Dropdown
                                                overlay={
                                                    <Menu>
                                                        <Menu.Item key="default">
                                                            <div
                                                                onClick={() => handleSelectChange("Ref 2 Type", "reference_2_type")}
                                                                onMouseEnter={() => setHoveredField('reference2')}
                                                                onMouseLeave={() => setHoveredField(null)} // Default value
                                                            >
                                                                Ref 2 Type
                                                            </div>
                                                        </Menu.Item>
                                                        {referencesData
                                                            ?.filter(
                                                                (rec) => rec.order_reference_type !== formValues.reference_2_type
                                                            )
                                                            .map((rec) => (
                                                                <Menu.Item key={rec.order_reference_type}>
                                                                    <div
                                                                        onClick={() =>
                                                                            handleSelectChange(rec.order_reference_type, "reference_2_type")
                                                                        }
                                                                        style={{
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        {rec.order_reference_type}
                                                                    </div>
                                                                </Menu.Item>
                                                            ))}
                                                    </Menu>
                                                }
                                                trigger={["click"]}
                                            >
                                                <div style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#576ec1" }}>
                                                    <span>{formValues.reference_2_type || "Ref Type"}</span>
                                                    <Icon style={{ marginLeft: "4px", color: "#576ec1" }} type="caret-down" />
                                                </div>
                                            </Dropdown>
                                            <span>
                                                {isFieldMandatory("reference_2")  && <span style={{ color: "red" }}>*</span>}
                                            </span>
                                        </div>
                                        {hoveredField === 'reference2' && <InfoTooltip label="Reference Types"
                                            icon={<RefNumTooltipIcon />}
                                            message={[
                                                "Reference Types are customizable labels set in Org. Settings > Order Settings, ideal for operations differing from industry standards."
                                            ]} />}

                                    </div>
                                </Col>

                                <Col span={13}>
                                    <ReusableInput
                                        data-testid="reference-2-input"
                                        name="reference_2"
                                        value={formValues.reference_2}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col span={11} style={{ lineHeight: "30px" }}
                                    onMouseEnter={() => setHoveredField('other')}
                                    onMouseLeave={() => setHoveredField(null)}>
                                    <div
                                        className="RefNumbersStyle"
                                        style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}
                                    >
                                        <span>Other</span>
                                        {hoveredField === 'other' && (
                                            <InfoTooltip
                                                label="Other References"
                                                icon={<RefNumTooltipIcon />}
                                                message={[
                                                    "Use 'Other' to input any reference numbers that don't fit into the predefined categories, you can comma separate multiple entries."
                                                ]}
                                            />
                                        )}
                                    </div>

                                </Col>
                                <Col span={13}>
                                    <Select
                                        data-testid="other-input"
                                         style={{
                                            width: "100%",
                                            border: "1px solid #8897a4",
                                            borderRadius: "4px",
                                        }}
                                        value={formValues.references || []}
                                        onChange={(val) =>
                                            handleSelectChange(val, "references")
                                        }
                                        mode="tags"
                                        maxTagCount={0}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}

                    <Row style={{ marginTop: "10px" }}>
                        <Col span={24}>
                        {!availableSpace && (
                            <div
                                className="button-2-large"
                                onClick={() => setShowReferences(!showReferences)}
                                style={{ cursor: "pointer", color: "#576ec1", marginTop: "8px" }}
                            >
                                {showReferences
                                    ? (
                                        <>
                                            See less
                                            <Icon style={{ marginLeft: "8px", color: "#576ec1" }} type="caret-up" />
                                        </>
                                    ) : (
                                        <>
                                            See All Reference Types
                                            <Icon style={{ marginLeft: "8px", color: "#576ec1" }} type="caret-down" />
                                        </>
                                    )}
                            </div>
                              )}
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    );
}

export default ReferenceNumberSection;
